import { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import cls from "classnames";

import { isImageOrVideo } from "@qoohoo/common/utils/url";
import MediaViewer from "./mediaViewer";
import * as Track from "@qoohoo/common/utils/analytics/track";

import "swiper/css";
import "swiper/css/navigation";
import { button } from "@qoohoo/common/constants/analytics";

const Preview = ({ url, selected }) => {
  const type = isImageOrVideo(url);

  return (
    <div className="relative w-16 h-16 rounded-lg overflow-hidden">
      {!selected && (
        <div className="absolute inset-0 z-[1] bg-black opacity-70"></div>
      )}
      {type === "img" ? (
        <img src={url} alt="media" className="w-full h-full object-cover" />
      ) : (
        <video
          src={url}
          alt="media"
          className="w-full h-full object-cover"
          preload="metadata"
        />
      )}
    </div>
  );
};

const DesktopCarousel = ({
  mediaUrls,
  mediaFileName,
  mediaFileSize,
  trackDetails,
  isInCourse,
  channelName,
  creatorName,
  showWatermark,
}) => {
  const [swiperRef, setSwiperRef] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  function trackPostClick() {
    Track.buttonClick(button.click_premium_post, {
      ...trackDetails,
      slide: currentSlide,
      post_url: mediaUrls[currentSlide],
    });
  }

  return (
    <div className={cls("relative", isInCourse ? "h-full" : "")}>
      {mediaUrls.length > 0 && mediaUrls[0] ? (
        <Swiper
          onSwiper={setSwiperRef}
          modules={[Navigation]}
          centeredSlides={true}
          pagination={{ clickable: true }}
          allowTouchMove={false}
          navigation={{
            prevEl: swiperRef?.navigation.$prevEl[0]?.current,
            nextEl: swiperRef?.navigation.$nextEl[0]?.current,
          }}
          onSlideChange={(swiper) => {
            setCurrentSlide(swiper.activeIndex);
          }}
          spaceBetween={2}
          className="custom-carousel"
          style={{
            position: "unset",
            width: "100% !important",
            height: isInCourse ? "100%" : "auto",
          }}
        >
          {mediaUrls.map((url, index) => {
            return (
              <SwiperSlide
                className={isInCourse ? "h-full" : ""}
                key={url + index}
              >
                <MediaViewer
                  url={url}
                  mediaFileName={mediaFileName}
                  mediaFileSize={mediaFileSize}
                  currentSlide={currentSlide}
                  imageClassName={cls(isInCourse ? "h-full" : "h-[245px]")}
                  videoClassName={cls(
                    "children:h-[245px]",
                    isInCourse ? "course" : ""
                  )}
                  trackImageClick={trackPostClick}
                  trackVideoClick={trackPostClick}
                  channelName={channelName}
                  creatorName={creatorName}
                  showWatermark={showWatermark}
                />
              </SwiperSlide>
            );
          })}
          {mediaUrls?.length > 1 && (
            <>
              <div
                className={cls(
                  "swiper-button-prev flex items-center absolute inset-0 cursor-pointer bg-[#373E43] rounded-full overflow-hidden !h-10 !w-10 select-none sm:-ml-1",
                  currentSlide === 0 && "!hidden"
                )}
                onClick={() => {
                  swiperRef?.slidePrev();

                  Track.buttonClick(button.prev_premium_post, {
                    ...trackDetails,
                    slide: currentSlide,
                    post_url: mediaUrls[currentSlide],
                  });
                }}
              >
                <img
                  src="/assets/icons/carousel-right.svg"
                  alt="previous slide button"
                  className="mr-1 rotate-180 sm:mr-0"
                />
              </div>
              <div
                className={cls(
                  "swiper-button-next flex items-center absolute inset-0 cursor-pointer bg-[#373E43] rounded-full !h-10 !w-10 select-none sm:-mr-1",
                  currentSlide === mediaUrls.length - 1 && "!hidden"
                )}
                onClick={() => {
                  swiperRef?.slideNext();

                  Track.buttonClick(button.next_premium_post, {
                    ...trackDetails,
                    slide: currentSlide,
                    post_url: mediaUrls[currentSlide],
                  });
                }}
              >
                <img
                  src="/assets/icons/carousel-right.svg"
                  alt="next slide button"
                  className="ml-1 sm:ml-0"
                />
              </div>
            </>
          )}
        </Swiper>
      ) : null}
    </div>
  );
};

const MobileCarousel = ({
  mediaUrls,
  mediaFileName,
  mediaFileSize,
  trackDetails,
  channelName,
  creatorName,
  showWatermark,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  function handleCarouselTouch(index) {
    setCurrentSlide(index);

    Track.buttonClick(button.click_premium_post_preview, {
      ...trackDetails,
      slide: index,
      post_url: mediaUrls[index],
    });
  }

  function trackPostTouch() {
    Track.buttonClick(button.click_premium_post, {
      ...trackDetails,
      slide: currentSlide,
      post_url: mediaUrls[currentSlide],
    });
  }

  return (
    <div>
      <div>
        <MediaViewer
          url={mediaUrls[currentSlide]}
          mediaFileName={mediaFileName}
          mediaFileSize={mediaFileSize}
          currentSlide={currentSlide}
          imageClassName="h-[197px]"
          videoClassName="children:h-[197px]"
          trackImageClick={trackPostTouch}
          trackVideoClick={trackPostTouch}
          channelName={channelName}
          creatorName={creatorName}
          showWatermark={showWatermark}
          />
      </div>
      {mediaUrls.length > 1 && (
        <div className="pt-8 pb-4 flex space-x-3 overflow-x-auto">
          {mediaUrls.map((url, index) => (
            <div key={url + index} onClick={() => handleCarouselTouch(index)}>
              <Preview url={url} selected={currentSlide === index} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MediaSwitcher = ({
  mediaUrls,
  documentFileName,
  documentFileSize,
  trackDetails,
  isInCourse,
  fullWidth,
  channelName,
  creatorName,
  showWatermark,
}) => {
  let mediaSwitcher = null;

  if (mediaUrls.length === 0) {
    mediaSwitcher = null;
  } else {
    mediaSwitcher = (
      <>
        <div
          className={cls(
            "hidden sm:block",
            isInCourse ? "!h-full" : "",
            fullWidth ? "w-full" : ""
          )}
        >
          <DesktopCarousel
            mediaUrls={mediaUrls}
            mediaFileName={documentFileName}
            mediaFileSize={documentFileSize}
            trackDetails={trackDetails}
            isInCourse={isInCourse}
            channelName={channelName}
            creatorName={creatorName}
            showWatermark={showWatermark}
            />
        </div>
        <div className="sm:hidden">
          <MobileCarousel
            mediaUrls={mediaUrls}
            mediaFileName={documentFileName}
            mediaFileSize={documentFileSize}
            trackDetails={trackDetails}
            channelName={channelName}
            creatorName={creatorName}
            showWatermark={showWatermark}
            />
        </div>
      </>
    );
  }

  return mediaSwitcher;
};

export default MediaSwitcher;
